// Human labels for the fiat-transactions module's three enums. Co-located
// with the enum types per `folder-structure.md` (module-local label maps
// live next to the consumer).

import type { StatusColor } from '@uiKit/components/StatusBadge.vue'
import {
  FIAT_TRANSACTION_REASON,
  FIAT_TRANSACTION_REFERENCE_TYPE,
  FIAT_TRANSACTION_STATUS,
  FIAT_TRANSACTION_TYPE,
  type FiatTransactionReason,
  type FiatTransactionReferenceType,
  type FiatTransactionStatus,
  type FiatTransactionType,
} from '../types/fiatTransactionsTypes'

const TRANSACTION_TYPE_LABEL: Record<FiatTransactionType, string> = {
  [FIAT_TRANSACTION_TYPE.CREDIT]: 'Credit',
  [FIAT_TRANSACTION_TYPE.DEBIT]: 'Debit',
}

export function transactionTypeLabel(value: FiatTransactionType): string {
  return TRANSACTION_TYPE_LABEL[value] ?? value
}

const TRANSACTION_REASON_LABEL: Record<FiatTransactionReason, string> = {
  [FIAT_TRANSACTION_REASON.DEPOSIT]: 'Deposit',
  [FIAT_TRANSACTION_REASON.WITHDRAWAL]: 'Withdrawal',
  [FIAT_TRANSACTION_REASON.INVESTMENT]: 'Investment',
  [FIAT_TRANSACTION_REASON.REFUND]: 'Refund',
  [FIAT_TRANSACTION_REASON.RENTAL_INCOME]: 'Rental income',
  [FIAT_TRANSACTION_REASON.RECHARGE_REVERSAL]: 'Recharge reversal',
  [FIAT_TRANSACTION_REASON.ORDER_REFUND]: 'Order refund',
  [FIAT_TRANSACTION_REASON.WITHDRAWAL_REVERSAL]: 'Withdrawal reversal',
  [FIAT_TRANSACTION_REASON.RENTAL_INCOME_REVERSAL]: 'Rental income reversal',
}

export function transactionReasonLabel(value: FiatTransactionReason): string {
  return TRANSACTION_REASON_LABEL[value] ?? value
}

const REFERENCE_TYPE_LABEL: Record<FiatTransactionReferenceType, string> = {
  [FIAT_TRANSACTION_REFERENCE_TYPE.RECHARGE_REQUEST]: 'Recharge',
  [FIAT_TRANSACTION_REFERENCE_TYPE.WITHDRAW_REQUEST]: 'Withdrawal',
  [FIAT_TRANSACTION_REFERENCE_TYPE.INVESTOR_RENTAL_PAYOUT]: 'Rental payout',
  [FIAT_TRANSACTION_REFERENCE_TYPE.ORDER]: 'Order',
  [FIAT_TRANSACTION_REFERENCE_TYPE.RENTAL_PERIOD]: 'Rental period',
  [FIAT_TRANSACTION_REFERENCE_TYPE.UNKNOWN]: 'Unknown',
}

export function referenceTypeLabel(value: FiatTransactionReferenceType): string {
  return REFERENCE_TYPE_LABEL[value] ?? value
}

// ─── Color hooks for status pills ────────────────────────────────────────────

const REFERENCE_TYPE_COLOR: Record<FiatTransactionReferenceType, StatusColor> = {
  [FIAT_TRANSACTION_REFERENCE_TYPE.RECHARGE_REQUEST]: 'info',
  [FIAT_TRANSACTION_REFERENCE_TYPE.WITHDRAW_REQUEST]: 'primary',
  [FIAT_TRANSACTION_REFERENCE_TYPE.INVESTOR_RENTAL_PAYOUT]: 'success',
  [FIAT_TRANSACTION_REFERENCE_TYPE.ORDER]: 'primary',
  [FIAT_TRANSACTION_REFERENCE_TYPE.RENTAL_PERIOD]: 'success',
  [FIAT_TRANSACTION_REFERENCE_TYPE.UNKNOWN]: 'neutral',
}

export function referenceTypeColor(value: FiatTransactionReferenceType): StatusColor {
  return REFERENCE_TYPE_COLOR[value] ?? 'neutral'
}

// ─── Transaction status ───────────────────────────────────────────────────────

const TRANSACTION_STATUS_LABEL: Record<FiatTransactionStatus, string> = {
  [FIAT_TRANSACTION_STATUS.COMPLETED]: 'Completed',
  [FIAT_TRANSACTION_STATUS.REJECTED]: 'Rejected',
}

export function transactionStatusLabel(value: FiatTransactionStatus): string {
  return TRANSACTION_STATUS_LABEL[value] ?? value
}

const TRANSACTION_STATUS_COLOR: Record<FiatTransactionStatus, StatusColor> = {
  [FIAT_TRANSACTION_STATUS.COMPLETED]: 'success',
  [FIAT_TRANSACTION_STATUS.REJECTED]: 'danger',
}

export function transactionStatusColor(value: FiatTransactionStatus): StatusColor {
  return TRANSACTION_STATUS_COLOR[value] ?? 'neutral'
}
